import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useDialog } from '../components/admin/Dialog';
import StripeProvider from '../components/StripeProvider';
import PaymentForm from '../components/PaymentForm';
import HelcimCardForm from '../components/HelcimCardForm';
import './Account.css';

// Tabs every signed-in customer sees.
const TABS_RETAIL = [
  { key: 'overview',      label: 'Overview' },
  { key: 'orders',        label: 'Orders' },
  { key: 'returns',       label: 'Returns' },
  { key: 'gradingSubs',   label: 'PSA submissions' },
  { key: 'storeCredit',   label: 'Store credit' },
  { key: 'addresses',     label: 'Addresses' },
  { key: 'subscriptions', label: 'Subscriptions' },
  { key: 'payment',       label: 'Payment methods' },
  { key: 'notifications', label: 'Notifications' },
  { key: 'loyalty',       label: 'Loyalty' },
  { key: 'referrals',     label: 'Referrals' },
  { key: 'privacy',       label: 'Privacy' },
];
// B2B / wholesale-only tab. Surfaced when the user has a business_name set
// on their account (Fox Shop, Matt, etc.).
const TAB_CSV       = { key: 'csvUpload', label: 'Quick CSV order' };
// Developer / API tab. Surfaced when the user has opted in via a profile
// flag (developer_enabled === 1) — keeps the retail UI clean.
const TAB_DEVELOPER = { key: 'developer', label: 'Developer' };

function buildTabs(user) {
  const tabs = [...TABS_RETAIL];
  // Insert B2B tab right after Orders so wholesale flow stays adjacent to
  // the order history they're cross-referencing against.
  if (user?.business_name) {
    tabs.splice(2, 0, TAB_CSV);
  }
  // Developer tab last, just before Privacy (legal block at the end).
  if (user?.developer_enabled) {
    tabs.splice(tabs.length - 1, 0, TAB_DEVELOPER);
  }
  return tabs;
}

const Account = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState('overview');

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (!token || !userData) { navigate('/login'); return; }
    try { setUser(JSON.parse(userData)); }
    catch { localStorage.removeItem('user'); navigate('/login'); return; }
  }, [navigate]);

  function handleLogout() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Tell the Header (and any other listener) to re-read session state
    // without a full page reload.
    window.dispatchEvent(new Event('auth-change'));
    navigate('/');
  }

  if (!user) return null;

  return (
    <div className="account-page">
      <div className="account-header">
        <h1>Welcome back, {user.first_name}</h1>
        <button onClick={handleLogout} className="btn btn-secondary">Logout</button>
      </div>

      <nav className="account-tabs" role="tablist">
        {buildTabs(user).map(t => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            className={`account-tab${tab === t.key ? ' account-tab--active' : ''}`}
            onClick={() => setTab(t.key)}
          >{t.label}</button>
        ))}
      </nav>

      <section className="account-tab-panel">
        {tab === 'overview'      && <Overview user={user} onTab={setTab} />}
        {tab === 'orders'        && <Orders />}
        {tab === 'csvUpload'     && <CsvUpload />}
        {tab === 'returns'       && <Returns />}
        {tab === 'gradingSubs'   && <GradingSubmissions />}
        {tab === 'storeCredit'   && <StoreCredit />}
        {tab === 'addresses'     && <Addresses />}
        {tab === 'subscriptions' && <Subscriptions />}
        {tab === 'payment'       && <PaymentMethods />}
        {tab === 'notifications' && <NotificationPrefs user={user} />}
        {tab === 'loyalty'       && <Loyalty user={user} />}
        {tab === 'referrals'     && <Referrals />}
        {tab === 'developer'     && <Developer />}
        {tab === 'privacy'       && <Privacy user={user} />}
      </section>
    </div>
  );
};

// -------- shared --------
function authedFetch(path, opts = {}) {
  const token = localStorage.getItem('token');
  return fetch(path, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(opts.headers || {}) },
  });
}

// -------- PSA submissions (G4 concierge) --------
const SUB_LABEL = {
  pending_payment: 'Awaiting payment', paid: 'Paid', received: 'Received by us',
  shipped_to_psa: 'Shipped to PSA', at_psa: 'At PSA', returned: 'Returned',
  completed: 'Completed', cancelled: 'Cancelled',
};
function GradingSubmissions() {
  const [rows, setRows] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState({});
  const [pay, setPay] = useState(null);      // { id, clientSecret, amount_cents }
  const [payErr, setPayErr] = useState(null);

  const reload = () => authedFetch('/api/grading/submissions/mine')
    .then(r => r.ok ? r.json() : [])
    .then(d => setRows(Array.isArray(d) ? d : []))
    .catch(() => setRows([]));

  useEffect(() => { reload(); }, []);

  const startPay = async (id) => {
    setPayErr(null);
    try {
      const r = await authedFetch(`/api/grading/submissions/${id}/pay`, { method: 'POST' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Could not start payment');
      setPay({ id, provider: d.provider || 'stripe', clientSecret: d.clientSecret, amount_cents: d.amount_cents });
    } catch (e) { setPayErr(e.message); }
  };

  // Both Stripe and Helcim widgets call this on success. Server flips the
  // submission to paid (idempotent; Stripe also confirms via webhook).
  const confirmPaid = async (id, ref) => {
    try {
      await authedFetch(`/api/grading/submissions/${id}/confirm-payment`, {
        method: 'POST', body: JSON.stringify({ transaction_id: ref || null }),
      });
    } catch (_) { /* webhook will reconcile */ }
    setPay(null);
    setRows(rs => (rs || []).map(s => s.id === id ? { ...s, status: 'paid' } : s));
    reload();
  };

  const toggle = async (id) => {
    if (openId === id) { setOpenId(null); return; }
    setOpenId(id);
    if (!detail[id]) {
      try {
        const r = await authedFetch(`/api/grading/submissions/${id}`);
        if (r.ok) { const data = await r.json(); setDetail(d => ({ ...d, [id]: data })); }
      } catch (_) {}
    }
  };

  if (!rows) return <div className="account-card"><p>Loading…</p></div>;
  return (
    <div className="account-card">
      <h2>PSA submissions</h2>
      {!rows.length && (
        <p style={{ color: '#666' }}>
          No submissions yet. Grade a card on the <Link to="/grade">AI grader</Link> — if it’s worth it, you can submit it to PSA through us.
        </p>
      )}
      {rows.map(s => (
        <div key={s.id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, marginBottom: 10 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <strong>#{s.id}</strong>
            <span className="badge" style={{ background: '#eef2ff', color: '#3b4cca', padding: '2px 8px', borderRadius: 12, fontSize: 12 }}>
              {SUB_LABEL[s.status] || s.status}
            </span>
            <span style={{ color: '#666', fontSize: 14 }}>{s.item_count} card{s.item_count === 1 ? '' : 's'} · {s.tier}</span>
            <span style={{ marginLeft: 'auto', fontWeight: 600 }}>${((s.total_cents || 0) / 100).toFixed(2)}</span>
            {s.status === 'pending_payment' && pay?.id !== s.id && (
              <button type="button" className="btn btn-primary" style={{ padding: '4px 10px' }} onClick={() => startPay(s.id)}>Pay now</button>
            )}
            <button type="button" className="btn btn-secondary" style={{ padding: '4px 10px' }} onClick={() => toggle(s.id)}>
              {openId === s.id ? 'Hide' : 'Details'}
            </button>
          </div>
          {s.tracking && <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>Tracking: {s.tracking}</div>}
          {pay?.id === s.id && (
            <div style={{ marginTop: 10, borderTop: '1px solid #f1f5f9', paddingTop: 10 }}>
              {pay.provider === 'helcim' ? (
                <HelcimCardForm
                  amount={(pay.amount_cents || 0) / 100}
                  currency="CAD"
                  orderNumber={`GS-${s.id}`}
                  onSuccess={({ transactionId }) => confirmPaid(s.id, transactionId)}
                  onError={(e) => setPayErr(e.message || 'Payment failed')}
                />
              ) : pay.clientSecret ? (
                <StripeProvider>
                  <PaymentForm
                    clientSecret={pay.clientSecret}
                    amount={(pay.amount_cents || 0) / 100}
                    buttonText="Pay concierge fee"
                    onSuccess={(pi) => confirmPaid(s.id, pi?.id)}
                    onError={(e) => setPayErr(e.message || 'Payment failed')}
                  />
                </StripeProvider>
              ) : (
                <p style={{ color: '#666', fontSize: 14 }}>
                  Card payment isn’t available right now — please contact us to settle the ${((pay.amount_cents || 0) / 100).toFixed(2)} fee.
                </p>
              )}
              <button type="button" className="btn btn-link" style={{ marginTop: 6 }} onClick={() => setPay(null)}>Cancel</button>
            </div>
          )}
          {payErr && pay?.id !== s.id && s.status === 'pending_payment' && (
            <div style={{ color: '#dc2626', fontSize: 13, marginTop: 4 }}>{payErr}</div>
          )}
          {openId === s.id && detail[s.id] && (
            <div style={{ marginTop: 10, borderTop: '1px solid #f1f5f9', paddingTop: 8 }}>
              {(detail[s.id].items || []).map(it => (
                <div key={it.id} style={{ display: 'flex', gap: 10, padding: '3px 0', fontSize: 14, flexWrap: 'wrap' }}>
                  <span style={{ minWidth: 150 }}>{it.card_name || `Report #${it.report_id}`}</span>
                  <span style={{ color: '#666' }}>predicted {it.predicted_grade ?? '—'}</span>
                  {it.actual_grade
                    ? <span style={{ color: '#059669' }}>PSA {it.actual_grade}{it.cert_number ? ` · cert ${it.cert_number}` : ''}</span>
                    : <span style={{ color: '#94a3b8' }}>pending</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// -------- Overview --------
function Overview({ user, onTab }) {
  const [recent, setRecent] = useState([]);
  const [credit, setCredit] = useState(null);
  // Tier 1 Item 7 — outstanding Net-30 invoices
  const [outstanding, setOutstanding] = useState([]);
  // Tier 2 Item 21 — pickups ready to collect
  const [pickups, setPickups] = useState([]);
  useEffect(() => {
    authedFetch('/api/users/orders')
      .then(r => r.ok ? r.json() : [])
      .then(d => setRecent(Array.isArray(d) ? d.slice(0, 3) : []))
      .catch(() => {});
    authedFetch('/api/account/store-credit')
      .then(r => r.ok ? r.json() : null)
      .then(d => d && setCredit(Number(d.balance) || 0))
      .catch(() => {});
    authedFetch('/api/orders/me/outstanding')
      .then(r => r.ok ? r.json() : [])
      .then(d => setOutstanding(Array.isArray(d) ? d : []))
      .catch(() => {});
    authedFetch('/api/orders/me/pickups')
      .then(r => r.ok ? r.json() : [])
      .then(d => setPickups(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);
  return (
    <div className="account-grid">
      <div className="account-card">
        <h2>Account</h2>
        <div className="info-row"><strong>Name:</strong><span>{user.first_name} {user.last_name}</span></div>
        <div className="info-row"><strong>Email:</strong><span>{user.email}</span></div>
        {user.phone && <div className="info-row"><strong>Phone:</strong><span>{user.phone}</span></div>}
        <BirthdayInline user={user} />
        <Link to="/account/profile" className="btn btn-primary">Edit profile</Link>
      </div>
      <div className="account-card">
        <h2>Quick links</h2>
        {credit != null && (
          <div
            style={{
              display: 'flex', alignItems: 'center', gap: 12,
              background: '#e6f3ec',
              border: '1px solid #b7dfc4',
              padding: '10px 14px',
              borderRadius: 6,
              marginBottom: 8,
              color: '#0d5c3a',
            }}
          >
            <strong style={{ flex: '0 0 auto' }}>Store credit:</strong>
            <span style={{ fontWeight: 700, fontSize: 16 }}>${credit.toFixed(2)}</span>
            <button
              type="button"
              onClick={() => onTab('storeCredit')}
              style={{
                marginLeft: 'auto',
                background: '#0d5c3a', color: '#fff',
                border: 'none', borderRadius: 4,
                padding: '6px 12px', fontSize: 13, fontWeight: 600,
                cursor: 'pointer',
              }}
            >View →</button>
          </div>
        )}
        {pickups.length > 0 && (
          <div style={{ background: '#dcfce7', padding: '8px 12px', borderRadius: 6, marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong style={{ color: '#166534' }}>Ready to collect: {pickups.filter(p => p.pickup_ready_at).length}</strong>
              <button className="quick-link" onClick={() => onTab('orders')}>View →</button>
            </div>
            {pickups.filter(p => p.pickup_ready_at).slice(0, 2).map(p => (
              <div key={p.id} style={{ fontSize: 13, marginTop: 4, color: '#166534' }}>
                Order #{p.id} · code <code style={{ fontWeight: 700, fontSize: 14 }}>{p.pickup_code}</code>
                {p.warehouse_name && <> · {p.warehouse_name}</>}
              </div>
            ))}
            {pickups.filter(p => !p.pickup_ready_at).length > 0 && (
              <small style={{ color: '#475569' }}>{pickups.filter(p => !p.pickup_ready_at).length} preparing</small>
            )}
          </div>
        )}
        {outstanding.length > 0 && (() => {
          const totalCents = outstanding.reduce((s, o) => s + Number(o.balance_cents || 0), 0);
          const overdue = outstanding.filter(o => o.terms_due_at && new Date(o.terms_due_at) < new Date());
          return (
            <div style={{ background: overdue.length ? '#fee2e2' : '#fff8e1', padding: '8px 12px', borderRadius: 6, marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <strong>Outstanding invoices:</strong>
                <span style={{ fontWeight: 700, color: overdue.length ? '#b91c1c' : '#7a4900' }}>${(totalCents/100).toFixed(2)}</span>
              </div>
              <small style={{ color: '#64748b' }}>
                {outstanding.length} invoice{outstanding.length === 1 ? '' : 's'}
                {overdue.length > 0 ? ` · ${overdue.length} overdue` : ''}
              </small>
              <button className="quick-link" onClick={() => onTab('orders')} style={{ display: 'block', marginTop: 4 }}>View orders →</button>
            </div>
          );
        })()}
        <nav>
          <button className="quick-link" onClick={() => onTab('orders')}>Order history →</button>
          <button className="quick-link" onClick={() => onTab('returns')}>Returns →</button>
          <button className="quick-link" onClick={() => onTab('addresses')}>Saved addresses →</button>
          <button className="quick-link" onClick={() => onTab('loyalty')}>Loyalty status →</button>
          <Link to="/wishlist" className="quick-link">Wishlist →</Link>
          <Link to="/saved-searches" className="quick-link">Saved searches →</Link>
        </nav>
      </div>
      <div className="account-card account-card--wide">
        <h2>Recent orders</h2>
        {recent.length === 0
          ? <p>No orders yet. <Link to="/shop">Start shopping</Link></p>
          : recent.map(o => (
              <div key={o.id} className="info-row">
                <strong>#{o.id}</strong>
                <span>{new Date(o.created_at).toLocaleDateString()}</span>
                <span className={`status-badge status-${o.status}`}>{o.status}</span>
                <span>${Number(o.grand_total).toFixed(2)}</span>
              </div>
            ))}
      </div>
    </div>
  );
}

// -------- Orders --------
function Orders() {
  const navigate = useNavigate();
  const cart = useCart();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [returnFor, setReturnFor] = useState(null);
  const [reorderBusy, setReorderBusy] = useState(null);
  const [reorderMsg, setReorderMsg] = useState('');

  // Tier 1 Item 12 — one-click reorder
  const reorder = async (orderId) => {
    setReorderBusy(orderId); setReorderMsg('');
    try {
      const r = await authedFetch(`/api/orders/${orderId}/reorder`, { method: 'POST' });
      const j = await r.json();
      if (!r.ok) { setReorderMsg(j.error || 'Reorder failed'); return; }
      let added = 0;
      for (const line of (j.lines || [])) {
        const res = await cart.addToCart({
          id: line.product_id, variant_id: line.variant_id || undefined,
          name: line.name, price: line.unit_price, stock: line.available_stock,
        }, line.quantity);
        if (res?.ok !== false) added++;
      }
      const skipped = (j.unresolved || []).length + ((j.lines || []).length - added);
      setReorderMsg(`Added ${added} item(s) to cart${skipped ? ` · ${skipped} skipped (out of stock or no longer available)` : ''}.`);
      if (added > 0) setTimeout(() => navigate('/cart'), 1500);
    } catch (e) { setReorderMsg(e.message); }
    finally { setReorderBusy(null); }
  };
  useEffect(() => {
    authedFetch('/api/users/orders')
      .then(r => r.ok ? r.json() : [])
      .then(d => { setOrders(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);
  if (loading) return <p>Loading…</p>;
  if (!orders.length) return <p>You haven't placed any orders yet.</p>;
  return (
    <div className="orders-list">
      {reorderMsg && <div style={{ marginBottom: 12, padding: 10, background: '#e6f3ec', borderRadius: 4 }}>{reorderMsg}</div>}
      {orders.map(order => {
        const status = (order.status || '').toLowerCase();
        const eligibleForReturn = ['shipped', 'delivered'].includes(status) &&
          (Date.now() - new Date(order.created_at).getTime()) / 86400_000 <= 30;
        const expanded = expandedId === order.id;
        return (
          <div key={order.id} className="order-card">
            <div className="order-header">
              <h3>Order #{order.id}</h3>
              <span className={`status-badge status-${order.status}`}>{order.status}</span>
            </div>
            <p className="order-date">{new Date(order.created_at).toLocaleDateString()}</p>
            <p className="order-total">Total: ${Number(order.grand_total).toFixed(2)}</p>
            <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
              <button className="btn btn-sm" onClick={() => setExpandedId(expanded ? null : order.id)}>
                {expanded ? 'Hide timeline' : 'View timeline'}
              </button>
              {['paid','processing','shipped','delivered'].includes(status) && (
                <button className="btn btn-sm" onClick={() => downloadInvoice(order)}>Download invoice</button>
              )}
              {eligibleForReturn && (
                <button className="btn btn-sm" onClick={() => setReturnFor(order)}>Request return</button>
              )}
              <button className="btn btn-sm btn-primary" disabled={reorderBusy === order.id} onClick={() => reorder(order.id)}>
                {reorderBusy === order.id ? 'Adding…' : 'Reorder'}
              </button>
            </div>
            {expanded && <OrderTimeline orderId={order.id} />}
          </div>
        );
      })}
      {returnFor && (
        <ReturnRequestModal
          order={returnFor}
          onClose={() => setReturnFor(null)}
          onDone={() => { setReturnFor(null); alert('Return requested. Check the Returns tab.'); }}
        />
      )}
    </div>
  );
}

function OrderTimeline({ orderId }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    authedFetch(`/api/account/orders/${orderId}/timeline`)
      .then(r => r.ok ? r.json() : Promise.reject('not available'))
      .then(setData).catch(e => setErr(String(e)));
  }, [orderId]);
  if (err) return <p style={{ color: '#888', fontSize: 13, marginTop: 8 }}>Timeline unavailable.</p>;
  if (!data) return <p style={{ color: '#888', fontSize: 13, marginTop: 8 }}>Loading timeline…</p>;
  return (
    <ol style={{ borderLeft: '2px solid #ddd', marginTop: 12, paddingLeft: 16, listStyle: 'none' }}>
      {data.events.map((e, i) => (
        <li key={i} style={{ marginBottom: 10, position: 'relative' }}>
          <span style={{ position: 'absolute', left: -22, top: 4, width: 10, height: 10, borderRadius: '50%', background: '#0d5c3a' }} />
          <div style={{ fontWeight: 600, fontSize: 13 }}>{e.label}</div>
          <div style={{ fontSize: 12, color: '#666' }}>
            {new Date(e.at).toLocaleString()}
            {e.details?.location && <> · {e.details.location}</>}
            {e.details?.description && <> · {e.details.description}</>}
          </div>
        </li>
      ))}
    </ol>
  );
}

function ReturnRequestModal({ order, onClose, onDone }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  async function submit() {
    if (reason.trim().length < 10) return setErr('Please describe the issue (at least 10 characters)');
    setBusy(true); setErr('');
    try {
      const r = await authedFetch('/api/account/returns', {
        method: 'POST',
        headers: { 'Idempotency-Key': `return-${order.id}-${Date.now()}` },
        body: JSON.stringify({ order_id: order.id, reason }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Failed to request return');
      onDone();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'grid', placeItems: 'center', zIndex: 9999 }}>
      <div style={{ background: 'var(--bg-card, #fff)', padding: 20, borderRadius: 8, maxWidth: 480, width: '90%' }}>
        <h3 style={{ margin: '0 0 12px' }}>Request return — Order #{order.id}</h3>
        <p style={{ fontSize: 13, color: '#666' }}>Tell us what's wrong. Admin will review and email you a shipping label within 24h.</p>
        <textarea
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder="e.g. card arrived damaged, wrong item, etc."
          rows={5}
          style={{ width: '100%', padding: 10, border: '1px solid #ddd', borderRadius: 4, fontSize: 14 }}
        />
        {err && <div style={{ color: '#c0392b', fontSize: 13, marginTop: 8 }}>{err}</div>}
        <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={busy}>{busy ? 'Submitting…' : 'Submit request'}</button>
        </div>
      </div>
    </div>
  );
}

// -------- Returns tab --------
function Returns() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    authedFetch('/api/account/returns').then(r => r.ok ? r.json() : []).then(setRows).catch(() => setRows([]));
  }, []);
  if (!rows) return <p>Loading…</p>;
  if (!rows.length) return <p>No returns on file. Request one from the Orders tab.</p>;
  return (
    <div className="orders-list">
      {rows.map(r => (
        <div key={r.id} className="order-card">
          <div className="order-header">
            <h3>Return #{r.id}</h3>
            <span className={`status-badge status-${r.status}`}>{r.status}</span>
          </div>
          <p className="order-date">For order #{r.order_id} · {new Date(r.created_at).toLocaleDateString()}</p>
          <p style={{ fontSize: 13, color: '#444' }}>{r.reason}</p>
        </div>
      ))}
    </div>
  );
}

// -------- Store credit tab --------
function StoreCredit() {
  const [data, setData] = useState(null);
  useEffect(() => {
    authedFetch('/api/account/store-credit').then(r => r.ok ? r.json() : null).then(setData).catch(() => {});
  }, []);
  if (!data) return <p>Loading…</p>;
  return (
    <div>
      <div className="account-card" style={{ marginBottom: 16 }}>
        <h2>Available balance</h2>
        <div style={{ fontSize: 32, fontWeight: 700, color: '#0d5c3a' }}>
          ${Number(data.balance).toFixed(2)}
        </div>
        <p style={{ color: '#666', fontSize: 13 }}>Applied automatically at checkout.</p>
      </div>
      <div className="account-card">
        <h2>History</h2>
        {data.history.length === 0 ? <p>No store credit activity yet.</p> : (
          <table style={{ width: '100%', fontSize: 13 }}>
            <thead><tr style={{ textAlign: 'left', color: '#666' }}><th>Date</th><th>Source</th><th>Reason</th><th style={{ textAlign: 'right' }}>Amount</th></tr></thead>
            <tbody>
              {data.history.map(h => (
                <tr key={h.id} style={{ borderTop: '1px solid #eee' }}>
                  <td>{new Date(h.created_at).toLocaleDateString()}</td>
                  <td style={{ textTransform: 'capitalize' }}>{h.source}</td>
                  <td>{h.reason || '—'}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600, color: Number(h.amount) >= 0 ? '#0d5c3a' : '#c0392b' }}>
                    {Number(h.amount) >= 0 ? '+' : ''}${Number(h.amount).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
async function downloadInvoice(order) {
  const r = await authedFetch(`/api/orders/${order.id}/invoice.pdf`);
  if (!r.ok) return alert('Invoice not available yet');
  const blob = await r.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `invoice-${order.order_number || order.id}.pdf`;
  a.click();
}

// -------- Addresses --------
function Addresses() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  async function load() {
    const r = await authedFetch('/api/users/addresses');
    setRows(r.ok ? await r.json() : []);
  }
  useEffect(() => { load(); }, []);
  async function save(addr) {
    const opts = { method: addr.id ? 'PUT' : 'POST', body: JSON.stringify(addr) };
    const path = addr.id ? `/api/users/addresses/${addr.id}` : '/api/users/addresses';
    const r = await authedFetch(path, opts);
    if (r.ok) { setEditing(null); load(); } else alert('Save failed');
  }
  async function remove(id) {
    if (!window.confirm('Delete this address?')) return;
    await authedFetch(`/api/users/addresses/${id}`, { method: 'DELETE' });
    load();
  }
  return (
    <div className="account-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>Saved addresses</h2>
        <button className="btn btn-primary" onClick={() => setEditing({ label: '', address_line1: '', city: '', province: 'ON', postal_code: '', country: 'CA', is_default: 0 })}>
          + Add address
        </button>
      </div>
      {rows.length === 0 && <p style={{ marginTop: 12 }}>No saved addresses yet.</p>}
      {rows.map(a => (
        <div key={a.id} className="info-row" style={{ flexWrap: 'wrap', gap: 8 }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <strong>{a.label || 'Address'}</strong> {a.is_default ? <em>(default)</em> : null}
            <div>{a.address_line1}{a.address_line2 ? `, ${a.address_line2}` : ''}</div>
            <div>{a.city}, {a.province} {a.postal_code} · {a.country}</div>
          </div>
          <button onClick={() => setEditing(a)}>Edit</button>
          <button onClick={() => remove(a.id)} style={{ color: '#c0392b' }}>Delete</button>
        </div>
      ))}
      {editing && (
        <AddressForm value={editing} onChange={setEditing} onCancel={() => setEditing(null)} onSave={() => save(editing)} />
      )}
    </div>
  );
}
function AddressForm({ value, onChange, onCancel, onSave }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div style={{ marginTop: 12, padding: 12, border: '1px solid #eee', borderRadius: 8 }}>
      <input placeholder="Label (Home, Work…)" value={value.label || ''} onChange={e => set('label', e.target.value)} />
      <input placeholder="Address line 1" value={value.address_line1 || ''} onChange={e => set('address_line1', e.target.value)} />
      <input placeholder="Address line 2 (optional)" value={value.address_line2 || ''} onChange={e => set('address_line2', e.target.value)} />
      <input placeholder="City" value={value.city || ''} onChange={e => set('city', e.target.value)} />
      <input placeholder="Province (e.g. ON)" maxLength={2} value={value.province || ''} onChange={e => set('province', e.target.value.toUpperCase())} />
      <input placeholder="Postal code" value={value.postal_code || ''} onChange={e => set('postal_code', e.target.value.toUpperCase())} />
      <input placeholder="Country (CA / US)" maxLength={2} value={value.country || 'CA'} onChange={e => set('country', e.target.value.toUpperCase())} />
      <label style={{ display: 'block', marginTop: 6 }}>
        <input type="checkbox" checked={!!value.is_default} onChange={e => set('is_default', e.target.checked ? 1 : 0)} /> Set as default
      </label>
      <div style={{ marginTop: 10 }}>
        <button className="btn btn-primary" onClick={onSave}>Save</button>{' '}
        <button onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// -------- Subscriptions --------
function Subscriptions() {
  const dialog = useDialog();
  const [subs, setSubs] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState('');
  const [swapping, setSwapping] = useState(null);   // sub being swapped
  const [editingBox, setEditingBox] = useState(null); // sub whose box is open
  const [boxData, setBoxData] = useState(null);     // { max_items, max_value_cents, items, total_cents }
  const [boxSearch, setBoxSearch] = useState('');
  const [boxResults, setBoxResults] = useState([]);
  const [history, setHistory] = useState({});       // { [sub_id]: [events] }

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 4500); };

  const reload = async () => {
    const d = await authedFetch('/api/subscriptions/me').then(r => r.ok ? r.json() : null).catch(() => null);
    setSubs(d && d.id ? [d] : []);
    setLoading(false);
  };
  useEffect(() => {
    reload();
    authedFetch('/api/subscriptions/plans').then(r => r.ok ? r.json() : []).then(setPlans).catch(() => setPlans([]));
  }, []);

  const post = async (path, body) => {
    setBusy(path);
    try {
      const r = await authedFetch(path, { method: 'POST', body: JSON.stringify(body || {}) });
      const j = await r.json();
      if (!r.ok) { flash(j.error || 'Failed'); return null; }
      return j;
    } finally { setBusy(null); }
  };
  const pause = async (id) => {
    const days = await dialog.prompt('Pause for how many days? (1-180)', '14', { title: 'Pause subscription' });
    if (!days) return;
    const r = await post(`/api/subscriptions/${id}/pause`, { days: Number(days) });
    if (r) { flash(`Paused until ${new Date(r.paused_until).toDateString()}`); reload(); }
  };
  const resume = async (id) => {
    const r = await post(`/api/subscriptions/${id}/resume`, {});
    if (r) { flash('Resumed'); reload(); }
  };
  const skipNext = async (id) => {
    const r = await post(`/api/subscriptions/${id}/skip-next`, { count: 1 });
    if (r) { flash(`Skip queued (${r.skip_next_count} pending)`); reload(); }
  };
  const cancel = async (id) => {
    if (!await dialog.confirm('Cancel this subscription? You can re-subscribe later.', { danger: true, okLabel: 'Cancel subscription' })) return;
    const r = await authedFetch(`/api/subscriptions/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status: 'cancelled' }) });
    if (r.ok) { flash('Cancelled'); reload(); }
  };
  const doSwap = async (newPlanId) => {
    const sub = swapping;
    setSwapping(null);
    const r = await post(`/api/subscriptions/${sub.id}/swap`, { new_plan_id: newPlanId });
    if (r) { flash(`Swapped to ${r.plan_name}`); reload(); }
  };
  const loadHistory = async (id) => {
    if (history[id]) { setHistory({ ...history, [id]: null }); return; }
    const rows = await authedFetch(`/api/subscriptions/${id}/events`).then(r => r.ok ? r.json() : []).catch(() => []);
    setHistory({ ...history, [id]: rows });
  };

  // Tier 2 Item 17 — Build-a-box editor
  const openBox = async (sub) => {
    setEditingBox(sub); setBoxData(null); setBoxSearch(''); setBoxResults([]);
    const r = await authedFetch(`/api/subscriptions/${sub.id}/box`);
    if (r.ok) setBoxData(await r.json());
    else { flash((await r.json().catch(() => ({}))).error || 'Failed to load box'); setEditingBox(null); }
  };
  const searchBoxProducts = async () => {
    if (!boxSearch.trim()) return setBoxResults([]);
    const r = await fetch(`/api/products?search=${encodeURIComponent(boxSearch)}&limit=10`);
    if (r.ok) setBoxResults(await r.json());
  };
  useEffect(() => {
    if (!editingBox) return;
    const t = setTimeout(searchBoxProducts, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [boxSearch]);

  const setBoxItemQty = (productId, qty) => {
    if (!boxData) return;
    const next = boxData.items.filter(i => i.product_id !== productId);
    if (qty > 0) {
      const existing = boxData.items.find(i => i.product_id === productId);
      if (existing) next.push({ ...existing, qty });
      else {
        // Will be hydrated from search result
        const hit = boxResults.find(p => p.id === productId);
        if (hit) next.push({ product_id: hit.id, qty, name: hit.name, price: hit.price, image_url: hit.image_url, sku: hit.sku, stock: hit.stock });
      }
    }
    const totalQty = next.reduce((s, i) => s + Number(i.qty), 0);
    const totalCents = next.reduce((s, i) => s + Math.round(Number(i.price || 0) * 100) * Number(i.qty || 1), 0);
    setBoxData({ ...boxData, items: next, items_total: totalQty, total_cents: totalCents });
  };
  const saveBox = async () => {
    if (!boxData) return;
    const r = await authedFetch(`/api/subscriptions/${editingBox.id}/box`, {
      method: 'PUT',
      body: JSON.stringify({ items: boxData.items.map(i => ({ product_id: i.product_id, qty: i.qty })) }),
    });
    const j = await r.json();
    if (!r.ok) { flash(j.error || 'Save failed'); return; }
    flash(`Box saved (${j.items_total} items · $${(j.total_cents / 100).toFixed(2)})`);
    setEditingBox(null);
  };

  if (loading) return <p>Loading…</p>;
  if (!subs.length) return (
    <div className="account-card">
      <h2>Subscriptions</h2>
      <p>You don't have any active subscriptions. <Link to="/subscribe">Browse plans →</Link></p>
    </div>
  );
  return (
    <div className="account-card">
      <h2>Your subscriptions</h2>
      {msg && <div style={{ padding: 8, background: '#e6f3ec', marginBottom: 12 }}>{msg}</div>}

      {subs.map(s => (
        <div key={s.id} style={{ borderTop: '1px solid #e2e8f0', paddingTop: 12, marginBottom: 12 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <strong>{s.plan_name || s.plan_slug}</strong>
              <div style={{ fontSize: 14, color: '#475569' }}>
                ${Number(s.price_per_period || s.price || 0).toFixed(2)} / {s.period_days || s.period || 30}d · <strong>{s.status}</strong>
                {Number(s.skip_next_count) > 0 && <span style={{ marginLeft: 6, padding: '2px 6px', background: '#fff8e1', color: '#92400e', fontSize: 11, borderRadius: 3 }}>{s.skip_next_count} skip pending</span>}
                {Number(s.prepaid_periods_remaining) > 0 && <span style={{ marginLeft: 6, padding: '2px 6px', background: '#dcfce7', color: '#166534', fontSize: 11, borderRadius: 3 }}>{s.prepaid_periods_remaining} prepaid cycles left</span>}
              </div>
              {s.next_ship_at && <div style={{ fontSize: 13, color: '#64748b' }}>Next cycle: {new Date(s.next_ship_at).toLocaleDateString()}</div>}
              {s.paused_until && <div style={{ fontSize: 13, color: '#92400e' }}>Paused until: {new Date(s.paused_until).toLocaleDateString()}</div>}
            </div>
            {s.status === 'active' && (
              <>
                <button onClick={() => pause(s.id)} disabled={!!busy}>Pause…</button>
                <button onClick={() => skipNext(s.id)} disabled={!!busy}>Skip next</button>
                <button onClick={() => setSwapping(s)} disabled={!!busy}>Swap plan…</button>
                {s.allow_build_a_box && <button onClick={() => openBox(s)} disabled={!!busy}>Edit box…</button>}
              </>
            )}
            {s.status === 'paused' && <button onClick={() => resume(s.id)} disabled={!!busy}>Resume now</button>}
            {s.status !== 'cancelled' && <button onClick={() => cancel(s.id)} disabled={!!busy} style={{ color: '#c0392b' }}>Cancel</button>}
            <button onClick={() => loadHistory(s.id)} style={{ marginLeft: 'auto' }}>{history[s.id] ? 'Hide history' : 'View history'}</button>
          </div>

          {history[s.id] && (
            <div style={{ marginTop: 8, padding: 10, background: '#f8fafc', borderRadius: 4, fontSize: 13 }}>
              {history[s.id].length === 0 ? <em>No events yet.</em> : (
                <table style={{ width: '100%', fontSize: 12 }}>
                  <tbody>
                    {history[s.id].map(e => (
                      <tr key={e.id}>
                        <td><small>{new Date(e.created_at).toLocaleString()}</small></td>
                        <td><strong>{e.type}</strong></td>
                        <td><code style={{ fontSize: 11 }}>{JSON.stringify(e.payload || {})}</code></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      ))}

      {/* Tier 2 Item 17 — Build-a-box editor drawer */}
      {editingBox && boxData && (
        <div onClick={() => setEditingBox(null)}
             style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 9999, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '5vh 0' }}>
          <div onClick={e => e.stopPropagation()}
               style={{ width: 'min(640px, 92vw)', maxHeight: '90vh', overflow: 'auto', background: '#fff', borderRadius: 12, padding: 24 }}>
            <h2 style={{ marginTop: 0 }}>Curate your next cycle</h2>
            <p style={{ color: '#64748b', fontSize: 13 }}>
              Up to <strong>{boxData.max_items}</strong> items
              {boxData.max_value_cents != null && <> · max value <strong>${(boxData.max_value_cents / 100).toFixed(2)}</strong></>}
              . Current: <strong>{boxData.items_total}</strong> items / <strong>${(boxData.total_cents / 100).toFixed(2)}</strong>
            </p>

            <input value={boxSearch} onChange={e => setBoxSearch(e.target.value)}
                   placeholder="Search products to add…" style={{ width: '100%', padding: 8, marginBottom: 8 }} />
            {boxResults.length > 0 && (
              <div style={{ maxHeight: 180, overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: 4, marginBottom: 12 }}>
                {boxResults.map(p => {
                  const inBox = boxData.items.find(i => i.product_id === p.id);
                  const qty = inBox?.qty || 0;
                  return (
                    <div key={p.id} style={{ display: 'flex', alignItems: 'center', padding: 6, gap: 8, borderBottom: '1px solid #f1f5f9' }}>
                      {p.image_url && <img src={p.image_url} alt="" style={{ width: 32, height: 32, objectFit: 'contain' }} />}
                      <span style={{ flex: 1, fontSize: 13 }}><strong>{p.name}</strong> <small style={{ color: '#94a3b8' }}>· ${Number(p.price).toFixed(2)}</small></span>
                      <button onClick={() => setBoxItemQty(p.id, Math.max(0, qty - 1))} disabled={qty <= 0}>−</button>
                      <span style={{ minWidth: 20, textAlign: 'center', fontWeight: 600 }}>{qty}</span>
                      <button onClick={() => setBoxItemQty(p.id, qty + 1)} disabled={boxData.items_total >= boxData.max_items}>+</button>
                    </div>
                  );
                })}
              </div>
            )}

            <h4 style={{ marginTop: 12 }}>Your box ({boxData.items_total}/{boxData.max_items})</h4>
            {boxData.items.length === 0 ? (
              <p style={{ color: '#94a3b8' }}>Empty — search above to add items.</p>
            ) : (
              <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                <tbody>
                  {boxData.items.map(i => (
                    <tr key={i.product_id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: 6 }}>
                        {i.image_url && <img src={i.image_url} alt="" style={{ width: 26, height: 26, objectFit: 'contain', verticalAlign: 'middle', marginRight: 8 }} />}
                        <strong>{i.name}</strong>
                      </td>
                      <td align="right" style={{ padding: 6 }}>${Number(i.price).toFixed(2)}</td>
                      <td align="center" style={{ padding: 6 }}>
                        <button onClick={() => setBoxItemQty(i.product_id, Math.max(0, i.qty - 1))}>−</button>
                        <span style={{ display: 'inline-block', minWidth: 24, textAlign: 'center' }}>{i.qty}</span>
                        <button onClick={() => setBoxItemQty(i.product_id, i.qty + 1)} disabled={boxData.items_total >= boxData.max_items}>+</button>
                      </td>
                      <td align="right" style={{ padding: 6 }}><strong>${(Number(i.price) * i.qty).toFixed(2)}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button onClick={() => setEditingBox(null)}>Cancel</button>
              <button className="btn-primary" onClick={saveBox} disabled={boxData.items_total === 0}>Save box</button>
            </div>
          </div>
        </div>
      )}

      {swapping && (
        <div onClick={() => setSwapping(null)}
             style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()}
               style={{ width: 'min(460px, 92vw)', background: '#fff', borderRadius: 12, padding: 24 }}>
            <h2 style={{ marginTop: 0 }}>Swap your plan</h2>
            <p style={{ color: '#64748b' }}>Switch <strong>{swapping.plan_name}</strong> to a different plan. Takes effect on the next cycle.</p>
            {plans.filter(p => p.id !== swapping.plan_id && p.active).map(p => (
              <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 8, border: '1px solid #e2e8f0', borderRadius: 4, marginBottom: 6 }}>
                <div>
                  <strong>{p.name}</strong>
                  <div style={{ fontSize: 12, color: '#64748b' }}>${Number(p.price_per_period).toFixed(2)} / {p.period_days}d</div>
                </div>
                <button onClick={() => doSwap(p.id)}>Switch</button>
              </div>
            ))}
            <div style={{ textAlign: 'right', marginTop: 12 }}>
              <button onClick={() => setSwapping(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// -------- Notifications --------
function NotificationPrefs({ user }) {
  const [prefs, setPrefs] = useState(null);
  useEffect(() => {
    authedFetch(`/api/notifications/preferences/${user.id}`)
      .then(r => r.ok ? r.json() : null)
      .then(setPrefs)
      .catch(() => setPrefs({}));
  }, [user.id]);
  if (!prefs) return <p>Loading…</p>;
  const toggle = (k) => setPrefs({ ...prefs, [k]: prefs[k] ? 0 : 1 });
  async function save() {
    const r = await authedFetch('/api/notifications/preferences', {
      method: 'POST', body: JSON.stringify({ ...prefs, user_id: user.id }),
    });
    if (r.ok) alert('Saved');
  }
  return (
    <div className="account-card">
      <h2>Email & alert preferences</h2>
      {[
        ['email_orders',     'Order confirmations & shipping updates'],
        ['email_marketing',  'Newsletter & promotions'],
        ['email_restock',    'Restock alerts on items I follow'],
        ['email_price_drop', 'Price-drop alerts on items I follow'],
        ['email_auctions',   'Auction outbid notifications'],
        ['sms_orders',       'SMS order updates (carrier rates may apply)'],
      ].map(([k, label]) => (
        <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0' }}>
          <input type="checkbox" checked={!!prefs[k]} onChange={() => toggle(k)} />
          <span>{label}</span>
        </label>
      ))}
      <button className="btn btn-primary" onClick={save} style={{ marginTop: 12 }}>Save preferences</button>
    </div>
  );
}

// -------- Loyalty --------
function Loyalty({ user }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    authedFetch(`/api/loyalty/user/${user.id}`)
      .then(r => r.ok ? r.json() : null)
      .then(setData)
      .catch(() => setData({}));
  }, [user.id]);
  if (!data) return <p>Loading…</p>;

  // Tier ladder (mirrors server loyalty TIERS). Progress is computed from
  // lifetime points (≈ lifetime dollars spent, 1pt/$1).
  const THRESHOLDS = [['Bronze', 0], ['Silver', 500], ['Gold', 1500], ['Platinum', 5000]];
  const lp = Number(data.lifetime_points || 0);
  let ci = 0;
  for (let i = 0; i < THRESHOLDS.length; i++) if (lp >= THRESHOLDS[i][1]) ci = i;
  const current = THRESHOLDS[ci];
  const next = THRESHOLDS[ci + 1] || null;
  const pct = next ? Math.min(100, Math.round(((lp - current[1]) / (next[1] - current[1])) * 100)) : 100;

  return (
    <div className="account-card">
      <h2>Loyalty</h2>
      <div className="info-row"><strong>Points balance:</strong><span>{data.available_points ?? data.points ?? 0}</span></div>
      <div className="info-row"><strong>Lifetime earned:</strong><span>{lp}</span></div>
      <div className="info-row"><strong>Tier:</strong><span style={{ fontWeight: 700 }}>{current[0]}</span></div>

      {/* Tier-progress bar */}
      <div style={{ margin: '12px 0' }}>
        <div style={{ height: 10, background: 'var(--border-color, #e2e8f0)', borderRadius: 999, overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: 'linear-gradient(90deg,#16a34a,#0d5c3a)', borderRadius: 999, transition: 'width .4s ease' }} />
        </div>
        <div style={{ fontSize: 13, color: '#666', marginTop: 6 }}>
          {next
            ? <>{Math.max(0, next[1] - lp)} more points to <strong>{next[0]}</strong>.</>
            : <>You’re at the top tier — <strong>Platinum</strong>. 🏆</>}
        </div>
      </div>

      <p style={{ marginTop: 10, color: '#666' }}>
        Earn 1 point per $1 spent. Redeem 100 points for $1 off at checkout.
        Higher tiers unlock automatic member discounts storewide.
      </p>
    </div>
  );
}

// -------- Payment methods (provider-agnostic Stripe + Moneris) --------
function PaymentMethods() {
  const [data, setData] = useState({ provider: '', cards: [], note: null });
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await authedFetch('/api/users/me/payment-methods');
      setData(r.ok ? await r.json() : { provider: '', cards: [] });
    } catch (_) { setData({ provider: '', cards: [], error: 'Could not load.' }); }
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  async function detach(id) {
    if (!window.confirm('Remove this card? You can add it again at checkout.')) return;
    const r = await authedFetch(`/api/users/me/payment-methods/${encodeURIComponent(id)}`, { method: 'DELETE' });
    if (r.ok) load();
    else alert((await r.json().catch(() => ({}))).error || 'Failed to remove card');
  }

  async function startAdd() {
    setAdding(true);
    try {
      const r = await authedFetch('/api/users/me/payment-methods/setup-intent', { method: 'POST', body: '{}' });
      const setup = r.ok ? await r.json() : null;
      if (!setup?.clientSecret) {
        alert(setup?.error || 'Card vault not available for the current payment provider.');
        setAdding(false);
        return;
      }
      // Stripe Elements integration is the existing checkout flow — for the
      // MVP we redirect the user to a /account/add-card page; if that page
      // doesn't exist yet we surface a helpful message instead of breaking.
      alert(`To complete card setup, head to checkout once and tick "Save card for later" — same Stripe form.\n\nSetupIntent created: ${setup.clientSecret.slice(0, 24)}…`);
    } finally { setAdding(false); }
  }

  if (loading) return <p>Loading…</p>;
  return (
    <div className="account-card">
      <h2>Saved payment methods</h2>
      <p style={{ color: '#666', fontSize: 13 }}>
        Active processor: <strong>{data.provider || 'unknown'}</strong>. Your card details are stored by the processor — we never see the full number.
      </p>
      {data.note && <p style={{ background: '#fff8e1', padding: 10, borderRadius: 6, fontSize: 13 }}>{data.note}</p>}

      {data.cards.length === 0 ? (
        <p>No saved cards yet. Add one at your next checkout (look for "Save card for later").</p>
      ) : (
        <div>
          {data.cards.map(c => (
            <div key={c.id} className="info-row" style={{ flexWrap: 'wrap', gap: 8 }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <strong style={{ textTransform: 'capitalize' }}>{c.brand || 'Card'}</strong>{' '}
                ending in <code>{c.last4}</code>
                <div style={{ fontSize: 12, color: '#666' }}>
                  Expires {String(c.exp_month).padStart(2, '0')}/{String(c.exp_year).slice(-2)}
                </div>
              </div>
              <button onClick={() => detach(c.id)} style={{ color: '#c0392b' }}>Remove</button>
            </div>
          ))}
        </div>
      )}

      <button className="btn btn-primary" disabled={adding} onClick={startAdd} style={{ marginTop: 12 }}>
        {adding ? 'Preparing…' : 'Add a payment method'}
      </button>
    </div>
  );
}

// -------- Privacy (PIPEDA: export + delete) --------
function Privacy({ user }) {
  const [stage, setStage] = useState('idle'); // idle | confirming | working | done
  const [busy, setBusy] = useState(false);

  async function downloadExport(format) {
    setBusy(true);
    try {
      const r = await authedFetch(`/api/privacy/me/export?format=${format}`);
      if (!r.ok) throw new Error('Export failed');
      const blob = await r.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `silvergames-data-${user.id}.${format}`;
      a.click();
    } catch (e) { alert(e.message); }
    setBusy(false);
  }

  async function deleteMyData() {
    setBusy(true);
    try {
      const r = await authedFetch('/api/privacy/me/anonymize-orders', { method: 'POST', body: '{}' });
      if (!r.ok) throw new Error('Delete request failed');
      setStage('done');
    } catch (e) { alert(e.message); }
    setBusy(false);
  }

  return (
    <div className="account-card">
      <h2>Your privacy data</h2>
      <p style={{ color: '#666' }}>
        Under PIPEDA (Canada's federal privacy law) you have the right to access and delete personal data we hold.
        Order records are retained for 6 years for tax compliance, but personal details are scrubbed when you delete.
      </p>

      <h3 style={{ marginTop: 18, fontSize: 15 }}>Download a copy of your data</h3>
      <p style={{ fontSize: 13, color: '#666' }}>
        Includes your profile, order history, addresses, reviews, wishlist, loyalty balance, and notifications.
      </p>
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-primary" disabled={busy} onClick={() => downloadExport('json')}>Download as JSON</button>
        <button className="btn"             disabled={busy} onClick={() => downloadExport('csv')}>Download as CSV (Excel)</button>
      </div>

      <h3 style={{ marginTop: 24, fontSize: 15 }}>Delete my personal data</h3>
      <p style={{ fontSize: 13, color: '#666' }}>
        This anonymizes your name, email, phone, and shipping addresses on past orders. Order line items + financial
        totals are kept for tax retention. <strong>This cannot be undone.</strong>
      </p>

      {stage === 'idle' && (
        <button className="btn btn-danger" onClick={() => setStage('confirming')}>I want to delete my data…</button>
      )}
      {stage === 'confirming' && (
        <div style={{ background: '#fff8e1', border: '1px solid #ffeaa7', borderRadius: 8, padding: 12, marginTop: 8 }}>
          <p style={{ margin: '0 0 10px', fontSize: 13 }}>
            Confirm: <strong>scrub all personal information from {user.email}</strong>?
            Your account will remain but identifiable details will be removed from past orders, addresses, and reviews.
          </p>
          <button className="btn btn-danger" onClick={deleteMyData} disabled={busy}>Yes, delete my data</button>{' '}
          <button className="btn" onClick={() => setStage('idle')}>Cancel</button>
        </div>
      )}
      {stage === 'done' && (
        <div style={{ background: '#e8f5e9', border: '1px solid #c8e6c9', borderRadius: 8, padding: 12, marginTop: 8, fontSize: 14 }}>
          ✓ Done. Your personal data has been scrubbed from past orders. A confirmation email has been sent.
        </div>
      )}
    </div>
  );
}

// Tier 1 Item 10 — Quick CSV order (account tab variant of QuickOrder.js)
function CsvUpload() {
  const [csv, setCsv] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [msg, setMsg] = useState('');

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setCsv(await f.text());
  };

  const upload = async () => {
    if (!csv.trim()) return;
    setBusy(true); setMsg(''); setResult(null);
    try {
      const r = await authedFetch('/api/orders/import-csv', {
        method: 'POST', headers: { 'Content-Type': 'text/csv' }, body: csv,
      });
      const j = await r.json();
      if (!r.ok) { setMsg(j.error || 'Failed'); return; }
      setResult(j);
    } catch (e) { setMsg(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="account-card account-card--wide">
      <h2>Quick CSV order</h2>
      <p style={{ color: '#64748b' }}>
        Upload or paste a CSV — header row required: <code>sku,quantity</code> (optional <code>variant_sku</code>).
        Resolves your list against catalog + lifetime limits and shows a preview before adding to cart.
      </p>
      <p>
        <a href="data:text/csv,sku,quantity,variant_sku%0ASET-001,5,%0ASET-002,3," download="quick-order-template.csv">Download template</a>
      </p>
      <input type="file" accept=".csv,text/csv,text/plain" onChange={onFile} style={{ marginBottom: 8 }} />
      <textarea value={csv} onChange={e => setCsv(e.target.value)} rows={8}
                placeholder="sku,quantity&#10;SET-001,5&#10;SET-002,3"
                style={{ width: '100%', fontFamily: 'monospace', fontSize: 13, padding: 8 }} />
      <div style={{ marginTop: 8 }}>
        <button className="btn btn-primary" disabled={busy || !csv.trim()} onClick={upload}>
          {busy ? 'Resolving…' : 'Resolve & preview'}
        </button>
        <Link to="/quick-order" className="btn" style={{ marginLeft: 8 }}>Or use the paste pad →</Link>
      </div>
      {msg && <div style={{ marginTop: 12, padding: 10, background: '#fff8e1' }}>{msg}</div>}
      {result && (
        <div style={{ marginTop: 16 }}>
          <p>
            <strong>{result.summary.resolved}</strong> of {result.summary.total_lines} resolved · {result.summary.total_units} units · subtotal ${result.summary.subtotal.toFixed(2)}
          </p>
          {result.unresolved.length > 0 && (
            <ul style={{ color: '#dc2626' }}>
              {result.unresolved.slice(0, 10).map((u, i) => <li key={i}><code>{u.sku}</code> × {u.quantity} — {u.error}</li>)}
              {result.unresolved.length > 10 && <li>+ {result.unresolved.length - 10} more</li>}
            </ul>
          )}
          <p style={{ color: '#64748b', fontSize: 13 }}>
            Use the <Link to="/quick-order">Quick Order pad</Link> to confirm + bulk-add to cart.
          </p>
        </div>
      )}
    </div>
  );
}

// Tier 3 Item 26 — Referrals tab
function Referrals() {
  const [data, setData] = React.useState(null);
  const [copied, setCopied] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    authedFetch('/api/referrals/me')
      .then(r => r.status === 404 ? null : r.json())
      .then(d => setData(d))
      .catch(e => setError(e.message));
  }, []);

  if (data === null && !error) return <p>Loading...</p>;
  if (!data) return <p style={{ color: '#888' }}>Referral program is not currently enabled.</p>;

  const copy = () => {
    navigator.clipboard.writeText(data.share_url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div>
      <h2>Refer friends, both get credit</h2>
      <p style={{ color: '#666' }}>
        Share your code below. When your friend signs up and places their first qualifying order,
        you both get store credit automatically.
      </p>

      <div style={{ background: '#f3f4f6', borderRadius: 8, padding: 16, marginTop: 12 }}>
        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Your referral code</div>
        <div style={{ fontFamily: 'monospace', fontSize: 28, fontWeight: 700, letterSpacing: 2 }}>{data.code}</div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
          <input value={data.share_url} readOnly style={{ flex: 1, padding: 6, fontFamily: 'monospace', fontSize: 12 }} />
          <button onClick={copy} className="btn btn-primary">{copied ? 'Copied!' : 'Copy link'}</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 16 }}>
        <Stat label="Friends referred" value={data.redemption_count} />
        <Stat label="Total rewards earned" value={`$${(data.rewards_total_cents / 100).toFixed(2)}`} />
      </div>

      <h3 style={{ marginTop: 24 }}>Recent referrals</h3>
      {!data.history.length && <p style={{ color: '#888' }}>No referrals yet — share your code!</p>}
      {data.history.length > 0 && (
        <table style={{ width: '100%', fontSize: 13, marginTop: 8 }}>
          <thead><tr style={{ background: '#f3f4f6' }}>
            <th style={{ padding: 6, textAlign: 'left' }}>Friend</th>
            <th>Status</th><th>Reward</th><th>Date</th>
          </tr></thead>
          <tbody>
            {data.history.map(h => (
              <tr key={h.id} style={{ borderBottom: '1px solid #eee' }}>
                <td style={{ padding: 6 }}>{h.referee_email_masked || '—'}</td>
                <td style={{ textTransform: 'capitalize' }}>{h.reward_status}</td>
                <td>{h.reward_cents ? `$${(h.reward_cents / 100).toFixed(2)}` : '—'}</td>
                <td>{new Date(h.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
      <div style={{ fontSize: 12, color: '#6b7280' }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700 }}>{value}</div>
    </div>
  );
}

// Tier 3 Item 34 — inline birthday capture (qualifies user for birthday-points cron)
function BirthdayInline({ user }) {
  const [val, setVal] = React.useState(user?.birthday ? String(user.birthday).slice(0, 10) : '');
  const [saved, setSaved] = React.useState(false);
  const [error, setError] = React.useState('');
  const save = async () => {
    setError('');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(val)) { setError('Use YYYY-MM-DD'); return; }
    try {
      const token = localStorage.getItem('token');
      const r = await fetch('/api/users/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          first_name: user.first_name, last_name: user.last_name, phone: user.phone || '',
          birthday: val,
        }),
      });
      if (!r.ok) throw new Error((await r.json()).error || 'Save failed');
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) { setError(e.message); }
  };
  if (user?.birthday) {
    return <div className="info-row"><strong>Birthday:</strong><span>{String(user.birthday).slice(0, 10)}</span></div>;
  }
  return (
    <div className="info-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 6, padding: '8px 0' }}>
      <strong>Birthday <span style={{ color: '#10b981', fontWeight: 'normal', fontSize: 12 }}>(unlock annual bonus points!)</span></strong>
      <div style={{ display: 'flex', gap: 6, width: '100%' }}>
        <input type="date" value={val} onChange={e => setVal(e.target.value)} style={{ flex: 1 }} />
        <button onClick={save} className="btn btn-sm btn-primary">Save</button>
      </div>
      {saved && <small style={{ color: '#16a34a' }}>Saved!</small>}
      {error && <small style={{ color: '#dc2626' }}>{error}</small>}
    </div>
  );
}

// Tier 4 Item 40 — Personal API tokens (Developer tab)
function Developer() {
  const [rows, setRows] = React.useState([]);
  const [issued, setIssued] = React.useState(null);
  const [form, setForm] = React.useState({ name: '', scopes: ['orders:read'], rate_limit_per_min: 60 });
  const [error, setError] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  const load = async () => {
    const r = await authedFetch('/api/api-tokens/me');
    if (r.ok) setRows(await r.json());
  };
  React.useEffect(() => { load(); }, []);

  const toggleScope = (s) => setForm(f => ({
    ...f,
    scopes: f.scopes.includes(s) ? f.scopes.filter(x => x !== s) : [...f.scopes, s]
  }));

  const issue = async () => {
    setError(''); setBusy(true);
    try {
      const r = await authedFetch('/api/api-tokens/me', { method: 'POST', body: JSON.stringify(form) });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Failed');
      setIssued(data);
      setForm({ name: '', scopes: ['orders:read'], rate_limit_per_min: 60 });
      load();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const revoke = async (id) => {
    if (!window.confirm('Revoke this token? Apps using it will start failing immediately.')) return;
    const r = await authedFetch(`/api/api-tokens/me/${id}`, { method: 'DELETE' });
    if (r.ok) load();
  };

  return (
    <div>
      <h2>Developer — API tokens</h2>
      <p style={{ color: '#64748b' }}>
        Personal Bearer tokens for the public REST API.
        Read the <a href="/docs/api/" target="_blank" rel="noreferrer">docs</a> for endpoint reference.
        Personal tokens are read-only.
      </p>

      {issued && (
        <div style={{ padding: 16, marginBottom: 16, background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 8 }}>
          <strong>Copy this token now — it will not be shown again:</strong>
          <pre style={{ background: '#fff', padding: 12, borderRadius: 4, marginTop: 8, fontSize: 13, overflow: 'auto' }}>{issued.token}</pre>
          <button onClick={() => { navigator.clipboard.writeText(issued.token); }} className="btn btn-sm">Copy</button>
          <button onClick={() => setIssued(null)} className="btn btn-sm" style={{ marginLeft: 8 }}>I've copied it</button>
        </div>
      )}

      <div style={{ padding: 16, background: '#f9fafb', borderRadius: 8, marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>Issue token</h3>
        <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Token name (e.g. My script)" style={{ width: '100%', marginBottom: 8 }} />
        <div style={{ marginBottom: 8 }}>
          <strong style={{ fontSize: 13 }}>Scopes:</strong>
          {['orders:read', 'products:read', 'inventory:read'].map(s => (
            <label key={s} style={{ marginLeft: 12, fontSize: 13 }}>
              <input type="checkbox" checked={form.scopes.includes(s)} onChange={() => toggleScope(s)} /> {s}
            </label>
          ))}
        </div>
        <button onClick={issue} disabled={busy} className="btn btn-primary">{busy ? 'Issuing…' : 'Generate token'}</button>
        {error && <p style={{ color: '#dc2626', marginTop: 8 }}>{error}</p>}
      </div>

      <h3>Your tokens</h3>
      {!rows.length && <p style={{ color: '#94a3b8' }}>No tokens issued yet.</p>}
      {rows.length > 0 && (
        <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
          <thead><tr style={{ background: '#f3f4f6' }}>
            <th style={{ padding: 6, textAlign: 'left' }}>Name</th>
            <th>Prefix</th><th>Scopes</th><th>Used</th><th>Status</th><th></th>
          </tr></thead>
          <tbody>
            {rows.map(t => {
              const revoked = !!t.revoked_at;
              const expired = t.expires_at && new Date(t.expires_at) <= new Date();
              return (
                <tr key={t.id} style={{ borderBottom: '1px solid #eee', opacity: revoked || expired ? 0.5 : 1 }}>
                  <td style={{ padding: 6 }}>{t.name}</td>
                  <td><code style={{ fontSize: 11 }}>{t.prefix}…</code></td>
                  <td><small>{(Array.isArray(t.scopes) ? t.scopes : []).join(', ')}</small></td>
                  <td style={{ textAlign: 'right' }}>{t.request_count || 0}</td>
                  <td>{revoked ? 'revoked' : (expired ? 'expired' : 'active')}</td>
                  <td>{!revoked && <button onClick={() => revoke(t.id)} style={{ color: '#dc2626' }}>Revoke</button>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default Account;
